import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceArea,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Loader2,
  ChevronRight,
  ArrowLeft,
  Lock,
  Download,
  FileText,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  Server,
  Printer,
  FileDown,
  LogOut,
  UserCircle,
  Database,
  ShieldCheck,
  CloudOff,
  HelpCircle,
  Clock,
  Activity,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { exportReportToPdf } from "@/lib/pdf";
import { HOSPITAL } from "@/lib/i18n";
import { useAuth } from "@/admin/AuthContext";
import { useLanguage } from "@/lib/LanguageContext";
import {
  ANALISIS_BY_SLUG,
  ANALISIS_CATEGORIES,
  PENUNJANG_CATEGORIES,
  SIPERKASA_HERO_IMG,
  formatPeriod,
  buildPdfUrl,
  MONTH_NAMES_ID,
} from "@/lib/laporan";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import SirsApiFlow from "@/pages/SirsApiFlow";

// =====================================================================
// AUTH GATE — redirects to /admin/login if not authenticated
// =====================================================================
// Logged-in user chip + logout — shown on SIPERKASA pages so operators (who
// have no admin panel) can sign out and switch accounts.
const SiperkasaUserChip = ({ lang }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user || typeof user !== "object") return null;
  const roleLabel =
    user.role === "superadmin"
      ? "Superadmin"
      : user.role === "siperkasa"
      ? "Operator SIPERKASA"
      : "Admin";
  const doLogout = () => {
    logout();
    navigate("/");
  };
  return (
    <div className="flex items-center gap-2 no-print" data-testid="siperkasa-user-chip">
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm">
        <UserCircle className="h-4 w-4 text-slate-400" />
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-700 max-w-[180px] truncate">
            {user.name || user.email}
          </div>
          <div className="text-[10px] text-slate-400">{roleLabel}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={doLogout}
        data-testid="siperkasa-logout-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition"
      >
        <LogOut className="h-3.5 w-3.5" />
        {lang === "en" ? "Sign out" : "Keluar"}
      </button>
    </div>
  );
};

const AuthGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useLanguage();

  if (user === null || user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-800" />
      </div>
    );
  }
  if (!user || typeof user !== "object") {
    return (
      <Navigate
        to={`/admin/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }
  const canAccess =
    user.role === "superadmin" ||
    user.role === "siperkasa" ||
    user.can_access_siperkasa !== false;
  if (!canAccess) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50" data-testid="siperkasa-forbidden">
        <Header />
        <main className="flex-1 pt-28 md:pt-32 pb-16">
          <div className="container-x max-w-2xl">
            <div className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="h-5 w-5" />
                <div className="font-heading font-semibold text-lg">Akses Terbatas</div>
              </div>
              <p className="text-sm">
                Akun Anda belum memiliki izin untuk membuka <b>SIPERKASA</b>. Silakan
                hubungi superadmin untuk meminta akses.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-amber-800 hover:text-amber-900"
              >
                <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  return children;
};

// =====================================================================
// SIPERKASA INDEX — elegant login-gated landing with data-source cards
// =====================================================================
const SiperkasaCard = ({ c, t, idx }) => {
  const Icon = c.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
    >
      <Link
        to={`/siperkasa/${c.slug}`}
        data-testid={`siperkasa-card-${c.slug}`}
        className="group block bg-white rounded-3xl border border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl transition-all overflow-hidden h-full"
      >
        <div className="relative h-40 overflow-hidden">
          <img
            src={c.img}
            alt={t.laporan[c.key_title]}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className={`absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r ${c.accent}`} />
        </div>
        <div className="p-6">
          <div className={`h-11 w-11 rounded-2xl ${c.tint} flex items-center justify-center mb-3 -mt-10 relative ring-4 ring-white`}>
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-800 transition">
            {t.laporan[c.key_title]}
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            {t.laporan[c.key_desc]}
          </p>
        </div>
      </Link>
    </motion.div>
  );
};

// =====================================================================
// SIPERKASA KPI SUMMARY — combined performance snapshot (latest month)
// =====================================================================
const KPI_META = {
  kunjungan: { unit_id: "pasien", unit_en: "patients", suffix: "", color: "#2563eb" },
  bor: { unit_id: "keterisian tempat tidur", unit_en: "bed occupancy", suffix: "%", color: "#0d9488" },
  pegawai: { unit_id: "pegawai aktif", unit_en: "active staff", suffix: "", color: "#059669" },
  lab: { unit_id: "pemeriksaan", unit_en: "examinations", suffix: "", color: "#7c3aed" },
  radiologi: { unit_id: "pemeriksaan", unit_en: "examinations", suffix: "", color: "#0891b2" },
  apotek: { unit_id: "lembar resep", unit_en: "prescriptions", suffix: "", color: "#db2777" },
};

const SIPERKASA_CURRENT_YEAR = new Date().getFullYear();
const SIPERKASA_YEAR_MIN = 2015;
const SIPERKASA_YEAR_MAX = SIPERKASA_CURRENT_YEAR + 1;
// Current year going back 5 years (6 options), newest first.
const siperkasaYearOptions = () =>
  Array.from({ length: 6 }, (_, i) => SIPERKASA_CURRENT_YEAR - i);

// Reusable Year picker: a dynamic dropdown (current year back 5) plus an
// "Other year…" option that reveals a free-type number input (2015..next year).
const YearSelect = ({ value, onChange, lang, testid = "siperkasa-year" }) => {
  const [custom, setCustom] = useState(false);
  const base = siperkasaYearOptions();
  // Keep a typed/selected out-of-list year visible & selected in the dropdown.
  const options = base.includes(value)
    ? base
    : [value, ...base].sort((a, b) => b - a);

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n) && n >= SIPERKASA_YEAR_MIN && n <= SIPERKASA_YEAR_MAX) {
      onChange(n);
      setCustom(false);
    } else {
      toast.error(
        lang === "en"
          ? `Year must be between ${SIPERKASA_YEAR_MIN}-${SIPERKASA_YEAR_MAX}`
          : `Tahun harus antara ${SIPERKASA_YEAR_MIN}-${SIPERKASA_YEAR_MAX}`
      );
    }
  };

  if (custom) {
    return (
      <div className="flex items-center gap-1">
        <input
          data-testid={`${testid}-custom-input`}
          type="number"
          min={SIPERKASA_YEAR_MIN}
          max={SIPERKASA_YEAR_MAX}
          defaultValue={value}
          autoFocus
          placeholder={lang === "en" ? "Type year" : "Ketik tahun"}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(e.currentTarget.value);
            if (e.key === "Escape") setCustom(false);
          }}
          onBlur={(e) => commit(e.currentTarget.value)}
          className="w-24 px-3 py-2 rounded-xl border border-blue-300 bg-white text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
        />
        <button
          type="button"
          data-testid={`${testid}-custom-cancel`}
          onClick={() => setCustom(false)}
          title={lang === "en" ? "Cancel" : "Batal"}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <select
      data-testid={`${testid}-select`}
      value={value}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustom(true);
          return;
        }
        onChange(Number(e.target.value));
      }}
      className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
    >
      {options.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
      <option value="__custom__">
        {lang === "en" ? "Other year…" : "Tahun lain…"}
      </option>
    </select>
  );
};
// Small chip that shows the LATEST period that actually has data in SIRS/SIMRS,
// so operators immediately know how up-to-date the SIPERKASA figures are.
const LatestPeriodChip = ({ sub, lang }) => {
  const { authAxios } = useAuth();
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    let alive = true;
    const params = sub ? { sub } : {};
    authAxios
      .get("/analisis/latest-period", { params })
      .then((r) => {
        if (alive) setPeriod(r.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [sub, authAxios]);

  if (!period || !period.period_label) return null;
  return (
    <span
      data-testid="siperkasa-latest-period"
      title={
        lang === "en"
          ? "The most recent period available in the hospital SIRS/SIMRS data"
          : "Periode terbaru yang tersedia pada data SIRS/SIMRS rumah sakit"
      }
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[11px] font-medium whitespace-nowrap"
    >
      <Clock className="h-3 w-3" />
      {lang === "en" ? "Latest data:" : "Data terakhir:"}{" "}
      <span className="font-semibold">{period.period_label}</span>
    </span>
  );
};



const SiperkasaKpiSummary = ({ t, lang }) => {
  const { authAxios } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourceVerdict, setSourceVerdict] = useState(null);
  const [year, setYear] = useState(SIPERKASA_CURRENT_YEAR);
  const [month, setMonth] = useState(""); // "" = latest
  const allCats = useMemo(
    () => [...ANALISIS_CATEGORIES, ...PENUNJANG_CATEGORIES],
    []
  );

  // One-time: default the summary year to the LATEST period present in
  // SIRS/SIMRS (anchor = kunjungan) so the snapshot shows the most recent
  // available data rather than an empty current calendar year.
  const initedSummaryYearRef = useRef(false);
  useEffect(() => {
    if (initedSummaryYearRef.current) return;
    let alive = true;
    authAxios
      .get("/analisis/latest-period")
      .then((r) => {
        const ly = Number(r.data?.year);
        if (!alive) return;
        initedSummaryYearRef.current = true;
        if (Number.isFinite(ly) && ly > 0) {
          setYear(ly);
          setMonth("");
        }
      })
      .catch(() => {
        if (alive) initedSummaryYearRef.current = true;
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { year };
    if (month !== "") params.month = month;
    authAxios
      .get("/analisis/summary", { params })
      .then((r) => {
        if (alive) setSummary(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const periods = summary?.periods || [];

  // Data-source verdict (live / demo / fallback) for the index warning banner.
  useEffect(() => {
    let alive = true;
    authAxios
      .get("/sirs/data-source")
      .then((r) => {
        if (alive) setSourceVerdict(r.data);
      })
      .catch(() => {
        if (alive) setSourceVerdict(null);
      });
    return () => {
      alive = false;
    };
  }, [authAxios]);

  const controls = (
    <div className="flex items-center gap-2" data-testid="siperkasa-summary-filter">
      <YearSelect
        value={year}
        lang={lang}
        testid="siperkasa-summary-year"
        onChange={(y) => {
          setYear(y);
          setMonth("");
        }}
      />
      <select
        data-testid="siperkasa-summary-month"
        value={month}
        onChange={(e) => setMonth(e.target.value === "" ? "" : Number(e.target.value))}
        className="min-w-[150px] px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
      >
        <option value="">{lang === "en" ? "Latest month" : "Bulan terbaru"}</option>
        {periods.map((p) => (
          <option key={`${p.year}-${p.month}`} value={p.month}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading && !summary) {
    return (
      <div className="mt-8 p-10 flex items-center justify-center text-slate-300" data-testid="siperkasa-summary-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!summary || !summary.cards) {
    return null;
  }

  const cards = allCats
    .map((c) => ({ cfg: c, kpi: summary.cards[c.slug] }))
    .filter((x) => x.kpi);

  const fmt = (n) =>
    typeof n === "number" ? n.toLocaleString(lang === "en" ? "en-US" : "id-ID") : n ?? "—";

  return (
    <div className="mt-8" data-testid="siperkasa-summary">
      <SourceWarningBanner verdict={sourceVerdict} lang={lang} />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
            {lang === "en" ? "Performance Snapshot" : "Ringkasan Kinerja"}
          </div>
          <p className="text-sm text-slate-500">
            {month === ""
              ? lang === "en"
                ? "Latest figures across all sources"
                : "Angka terbaru dari seluruh sumber data"
              : lang === "en"
              ? "Figures across all sources"
              : "Angka dari seluruh sumber data"}
            {summary.period_label ? ` — ${summary.period_label}` : ""}
          </p>
        </div>
        {controls}
      </div>

      {cards.length === 0 ? (
        <div
          data-testid="siperkasa-summary-empty"
          className="p-8 rounded-2xl bg-white border border-dashed border-slate-200 text-center text-sm text-slate-400"
        >
          {lang === "en"
            ? "No data available for this period."
            : "Belum ada data untuk periode ini."}
        </div>
      ) : (
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map(({ cfg, kpi }, idx) => {
          const Icon = cfg.icon;
          const meta = KPI_META[cfg.slug] || {};
          const growth = kpi.growth_pct;
          const up = typeof growth === "number" && growth >= 0;
          return (
            <motion.div
              key={cfg.slug}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link
                to={`/siperkasa/${cfg.slug}`}
                data-testid={`siperkasa-kpi-${cfg.slug}`}
                className="group block bg-white rounded-2xl border border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-lg transition-all p-5 h-full"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`h-9 w-9 rounded-xl ${cfg.tint} flex items-center justify-center`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  {typeof growth === "number" && (
                    <span
                      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                        up ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
                      }`}
                    >
                      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {up ? "+" : ""}
                      {growth}%
                    </span>
                  )}
                </div>
                <div className="font-heading text-2xl font-bold text-slate-900 leading-none">
                  {fmt(kpi.value)}
                  {meta.suffix ? <span className="text-lg">{meta.suffix}</span> : null}
                </div>
                {Array.isArray(kpi.trend) && kpi.trend.length > 1 && (
                  <div
                    className="h-9 mt-2 -mx-1"
                    data-testid={`siperkasa-kpi-spark-${cfg.slug}`}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={kpi.trend} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                        <defs>
                          <linearGradient id={`spark-${cfg.slug}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={meta.color} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={meta.color} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={meta.color}
                          strokeWidth={2}
                          fill={`url(#spark-${cfg.slug})`}
                          dot={false}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="text-xs font-medium text-slate-600 mt-1 group-hover:text-blue-800 transition">
                  {t.laporan[cfg.key_title]}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {lang === "en" ? meta.unit_en : meta.unit_id}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      )}
    </div>
  );
};

// =====================================================================
// SIRS DISRUPTION BANNER — shown when "Mode SIRS API" is ON but the live
// SIRS endpoint is unreachable. General reassuring copy for everyone;
// technical detail (endpoint + reason) is added for superadmin only.
// =====================================================================
const SirsDisruptionBanner = ({ lang }) => {
  const { authAxios, user } = useAuth();
  const [status, setStatus] = useState(null);
  const isSuper = user && user.role === "superadmin";

  useEffect(() => {
    let alive = true;
    const load = () =>
      authAxios
        .get("/sirs/status")
        .then((r) => {
          if (alive) setStatus(r.data);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status || !status.disrupted) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="sirs-disruption-banner"
      className="mb-6 overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 shadow-sm"
    >
      <div className="flex items-start gap-4 p-5">
        <div className="relative shrink-0">
          <span className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping" />
          <div className="relative h-11 w-11 rounded-full bg-amber-100 ring-1 ring-amber-300 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-amber-900">
              {lang === "en"
                ? "Temporary System Disruption"
                : "Gangguan Sistem Sementara"}
            </h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-amber-200">
              {lang === "en" ? "Live data offline" : "Data langsung terputus"}
            </span>
          </div>
          <p className="text-sm text-amber-800/90 mt-1 leading-relaxed">
            {lang === "en"
              ? "The connection to the Hospital Information System (SIRS) is currently interrupted. The figures shown may be temporary or not the latest. Our team is working to restore it — please try again shortly."
              : "Koneksi ke Sistem Informasi Rumah Sakit (SIRS) sedang terganggu. Angka yang ditampilkan mungkin bersifat sementara atau belum diperbarui. Tim kami sedang memulihkannya — silakan coba beberapa saat lagi."}
          </p>
          {isSuper && (status.base_url || status.reason) && (
            <div
              data-testid="sirs-disruption-technical"
              className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 text-[11px]"
            >
              <span className="inline-flex items-center gap-1.5 text-amber-700 font-semibold uppercase tracking-wider">
                <Server className="h-3 w-3" /> Superadmin
              </span>
              <code className="px-2 py-1 rounded bg-white/70 border border-amber-200 text-amber-900 font-mono break-all">
                {status.base_url}
                {status.reason ? ` — ${status.reason}` : ""}
              </code>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const SiperkasaIndex = () => {
  const { t, lang } = useLanguage();
  return (
    <AuthGate>
      <div className="min-h-screen flex flex-col bg-slate-50" data-testid="siperkasa-index">
        <Header />
        <main className="flex-1 pt-28 md:pt-32 pb-16">
          <div className="container-x">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Link to="/" className="hover:text-blue-800">{t.nav.home}</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-blue-800 font-medium">{t.nav.siperkasa}</span>
              </div>
              <div className="flex items-center gap-2">
                <LatestPeriodChip lang={lang} />
                <SiperkasaUserChip lang={lang} />
              </div>
            </div>

            <SirsDisruptionBanner lang={lang} />

            {/* Hero */}
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-900 text-white shadow-2xl ring-1 ring-white/10">
              <div className="absolute inset-0 opacity-20">
                <img
                  src={SIPERKASA_HERO_IMG}
                  alt="Ilustrasi kegiatan rumah sakit"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Readability overlay — stronger on the left where the text sits */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/85 to-blue-950/20" />
              <div className="absolute inset-0 bg-blue-950/30" />
              <div className="relative grid md:grid-cols-2 gap-6 items-center p-8 md:p-12">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 ring-1 ring-amber-300/30 text-[11px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-5 backdrop-blur-sm">
                    <Lock className="h-3 w-3" />
                    {t.laporan.siperkasa_overline}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-10 w-1.5 rounded-full bg-gradient-to-b from-amber-300 to-amber-500" />
                    <h1
                      className="font-heading text-5xl md:text-7xl font-extrabold tracking-tight leading-none bg-gradient-to-br from-white via-blue-50 to-amber-200 bg-clip-text text-transparent"
                      style={{ textShadow: "0 2px 20px rgba(0,0,0,0.25)" }}
                    >
                      SIPERKASA
                    </h1>
                  </div>
                  <p className="text-white text-base md:text-xl font-semibold mb-4 drop-shadow">
                    {t.laporan.siperkasa_title}
                  </p>
                  <p className="text-blue-100/90 text-sm md:text-base leading-relaxed max-w-xl">
                    {t.laporan.siperkasa_subtitle}
                  </p>
                </div>
                <div className="hidden md:block relative">
                  <img
                    src={SIPERKASA_HERO_IMG}
                    alt="Ilustrasi kegiatan rumah sakit"
                    className="w-full rounded-2xl shadow-2xl ring-1 ring-white/20"
                    data-testid="siperkasa-hero-img"
                  />
                </div>
              </div>
            </div>

            {/* Combined KPI summary — latest month across all sources */}
            <SiperkasaKpiSummary t={t} lang={lang} />

            {/* Data-source cards — core */}
            <div className="mt-10">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4">
                {t.laporan.siperkasa_sources}
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {ANALISIS_CATEGORIES.map((c, idx) => (
                  <SiperkasaCard key={c.slug} c={c} t={t} idx={idx} />
                ))}
              </div>
            </div>

            {/* Fasilitas Penunjang */}
            <div className="mt-12">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4">
                {t.laporan.siperkasa_penunjang}
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {PENUNJANG_CATEGORIES.map((c, idx) => (
                  <SiperkasaCard key={c.slug} c={c} t={t} idx={idx} />
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    </AuthGate>
  );
};

// Backward-compat alias (old name).
export const AnalisisIndex = SiperkasaIndex;

// =====================================================================
// KUNJUNGAN ANALYTICS view
// =====================================================================
// Rentang periode dinamis untuk judul grafik tren (ikuti data aktual, bukan hardcoded).
// Menerima array baris yang punya field `periode` (mis. "Jan 26"), mengembalikan
// "awal – akhir" (atau satu periode bila hanya 1 baris, "" bila kosong).
const periodeRangeLabel = (rows) => {
  const arr = Array.isArray(rows) ? rows.filter((r) => r && r.periode) : [];
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0].periode;
  return `${arr[0].periode} – ${arr[arr.length - 1].periode}`;
};

const KunjunganAnalisisView = ({ entry, all, t, lang, breakdownTitle }) => {
  const d = entry?.data || {};
  const ralan = d.breakdown_ralan || d.breakdown_poli || [];
  const ranap = d.breakdown_ranap || [];
  const poli = ralan;
  const hasRanap = Array.isArray(ranap) && ranap.length > 0;
  const totalRalan = typeof d.total_ralan === "number" ? d.total_ralan : ralan.reduce((s, x) => s + (x.visits || 0), 0);
  const totalRanap = typeof d.total_ranap === "number" ? d.total_ranap : ranap.reduce((s, x) => s + (x.visits || 0), 0);
  const totalBaru = typeof d.total_baru === "number" ? d.total_baru : ralan.reduce((s, x) => s + (x.baru || 0), 0);
  const totalLama = typeof d.total_lama === "number" ? d.total_lama : ralan.reduce((s, x) => s + (x.lama || 0), 0);
  const hasBaruLama = ralan.length > 0 && (ralan[0].baru !== undefined || ralan[0].lama !== undefined);
  const baruPct = totalRalan > 0 ? (totalBaru / totalRalan) * 100 : 0;
  const lamaPct = totalRalan > 0 ? (totalLama / totalRalan) * 100 : 0;
  const weekday = d.weekday_pattern || [];
  const yoy = d.yoy || {};
  const growth = yoy.growth_pct || 0;
  const trend = [...(all || [])]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      return {
        periode: `${mn} ${String(x.year).slice(-2)}`,
        Total: x.data?.total || 0,
      };
    });

  // Tren persentase pasien baru (rawat jalan) beberapa bulan terakhir
  const trendBaru = [...(all || [])]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .filter((x) => (x.data?.total_ralan || 0) > 0 && x.data?.total_baru !== undefined)
    .map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      const tr = x.data.total_ralan || 0;
      const tb = x.data.total_baru || 0;
      return {
        periode: `${mn} ${String(x.year).slice(-2)}`,
        pct: tr > 0 ? Number(((tb / tr) * 100).toFixed(1)) : 0,
      };
    });
  const showTrendBaru = hasBaruLama && trendBaru.length >= 2;

  // Poliklinik dengan proporsi pasien baru tertinggi (min. volume agar tak bias sampel kecil)
  const topBaruPoli = (() => {
    if (!hasBaruLama || ralan.length === 0) return null;
    const minVisits = Math.max(10, totalRalan * 0.02);
    let pool = ralan.filter((x) => (x.visits || 0) >= minVisits);
    if (pool.length === 0) pool = ralan;
    let best = null;
    for (const x of pool) {
      const v = x.visits || 0;
      const p = v > 0 ? (x.baru || 0) / v : 0;
      if (!best || p > best._p) best = { ...x, _p: p, pct: p * 100 };
    }
    return best;
  })();

  // Rawat Inap: proporsi pasien baru vs lama + bangsal dengan proporsi baru tertinggi
  const hasRanapBaruLama = hasRanap && (ranap[0].baru !== undefined || ranap[0].lama !== undefined);
  const totalRanapBaru = ranap.reduce((s, x) => s + (x.baru || 0), 0);
  const totalRanapLama = ranap.reduce((s, x) => s + (x.lama || 0), 0);
  const baruRanapPct = totalRanap > 0 ? (totalRanapBaru / totalRanap) * 100 : 0;
  const lamaRanapPct = totalRanap > 0 ? (totalRanapLama / totalRanap) * 100 : 0;
  const topBaruRanap = (() => {
    if (!hasRanapBaruLama || ranap.length === 0) return null;
    const minVisits = Math.max(5, totalRanap * 0.02);
    let pool = ranap.filter((x) => (x.visits || 0) >= minVisits);
    if (pool.length === 0) pool = ranap;
    let best = null;
    for (const x of pool) {
      const v = x.visits || 0;
      const p = v > 0 ? (x.baru || 0) / v : 0;
      if (!best || p > best._p) best = { ...x, _p: p, pct: p * 100 };
    }
    return best;
  })();

  // Tren hunian Rawat Inap per bangsal (multi-garis, beberapa bulan terakhir)
  const RANAP_COLORS = ["#0d9488", "#4f46e5", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9"];
  const ranapSeries = (() => {
    const months = [...(all || [])]
      .sort((a, b) => a.year - b.year || a.month - b.month)
      .filter((x) => Array.isArray(x.data?.breakdown_ranap) && x.data.breakdown_ranap.length > 0);
    if (months.length < 2) return null;
    const agg = {};
    months.forEach((x) => x.data.breakdown_ranap.forEach((b) => { agg[b.name] = (agg[b.name] || 0) + (b.visits || 0); }));
    const topNames = Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 6).map((e) => e[0]);
    const data = months.map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      const map = Object.fromEntries(x.data.breakdown_ranap.map((b) => [b.name, b.visits || 0]));
      const row = { periode: `${mn} ${String(x.year).slice(-2)}` };
      topNames.forEach((n) => { row[n] = map[n] || 0; });
      return row;
    });
    return { data, names: topNames };
  })();
  const showRanapTrend = hasRanap && ranapSeries && ranapSeries.data.length >= 2;

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="analisis-kunjungan-kpis" data-pdf-keep>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.this_year}</div>
          <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums">{(yoy.this_year || 0).toLocaleString("id-ID")}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.last_year}</div>
          <div className="font-heading text-2xl font-bold text-slate-500 tabular-nums">{(yoy.last_year || 0).toLocaleString("id-ID")}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.yoy_growth}</div>
          <div className={`font-heading text-2xl font-bold tabular-nums inline-flex items-center gap-2 ${growth >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {growth >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            {growth > 0 ? "+" : ""}{growth}%
          </div>
        </div>
      </div>

      {/* Jenis kunjungan: Rawat Jalan vs Rawat Inap */}
      {hasRanap && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="analisis-kunjungan-jenis">
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl border border-indigo-100 p-5" data-testid="analisis-kunjungan-total-ralan">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-indigo-600 mb-1">
              <span className="h-2.5 w-2.5 rounded-full bg-indigo-500" />
              {t.laporan.total_ralan}
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums">{(totalRalan || 0).toLocaleString("id-ID")}</div>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl border border-teal-100 p-5" data-testid="analisis-kunjungan-total-ranap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-teal-600 mb-1">
              <span className="h-2.5 w-2.5 rounded-full bg-teal-500" />
              {t.laporan.total_ranap}
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums">{(totalRanap || 0).toLocaleString("id-ID")}</div>
          </div>
        </div>
      )}

      {/* Rasio Pasien Baru vs Lama (indikator pertumbuhan pasien rawat jalan) */}
      {hasBaruLama && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-rasio" data-pdf-keep>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">{t.laporan.rasio_baru_lama}</h3>
            <span className="text-xs text-slate-500">{t.laporan.rasio_baru_lama_desc}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4" data-testid="rasio-baru">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-600 mb-1">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                {t.laporan.pasien_baru}
              </div>
              <div className="font-heading text-3xl font-bold text-emerald-700 tabular-nums" data-testid="rasio-baru-pct">{baruPct.toFixed(1)}%</div>
              <div className="text-sm text-slate-500 tabular-nums mt-0.5">{(totalBaru || 0).toLocaleString("id-ID")} {t.laporan.pasien}</div>
            </div>
            <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4" data-testid="rasio-lama">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-indigo-600 mb-1">
                <span className="h-2.5 w-2.5 rounded-full bg-indigo-500" />
                {t.laporan.pasien_lama}
              </div>
              <div className="font-heading text-3xl font-bold text-indigo-700 tabular-nums" data-testid="rasio-lama-pct">{lamaPct.toFixed(1)}%</div>
              <div className="text-sm text-slate-500 tabular-nums mt-0.5">{(totalLama || 0).toLocaleString("id-ID")} {t.laporan.pasien}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.total_ralan}</div>
              <div className="font-heading text-3xl font-bold text-slate-800 tabular-nums">{(totalRalan || 0).toLocaleString("id-ID")}</div>
              <div className="text-sm text-slate-500 mt-0.5">{t.laporan.pasien}</div>
            </div>
          </div>
          {/* Stacked ratio bar */}
          <div className="h-4 w-full rounded-full overflow-hidden flex bg-slate-100" aria-hidden="true">
            <div className="h-full bg-emerald-500" style={{ width: `${baruPct}%` }} title={`${t.laporan.pasien_baru} ${baruPct.toFixed(1)}%`} />
            <div className="h-full bg-indigo-500" style={{ width: `${lamaPct}%` }} title={`${t.laporan.pasien_lama} ${lamaPct.toFixed(1)}%`} />
          </div>

          {/* Sorot poliklinik dengan proporsi pasien baru tertinggi */}
          {topBaruPoli && (
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="analisis-kunjungan-top-poli">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold px-2.5 py-1">
                <Star className="h-3.5 w-3.5" /> {t.laporan.top_poli_badge}
              </span>
              <div className="min-w-0">
                <div className="text-sm text-slate-600">{t.laporan.top_poli_label}</div>
                <div className="font-heading text-base font-bold text-slate-900 truncate">
                  {topBaruPoli.name} — <span className="text-amber-700 tabular-nums">{topBaruPoli.pct.toFixed(1)}%</span> {t.laporan.pasien_baru.toLowerCase()}
                </div>
              </div>
              <div className="ml-auto text-right text-sm text-slate-500 tabular-nums">
                {(topBaruPoli.baru || 0).toLocaleString("id-ID")} / {(topBaruPoli.visits || 0).toLocaleString("id-ID")} {t.laporan.pasien}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rasio Pasien Baru Rawat Inap (indikator pertumbuhan pasien rawat inap) */}
      {hasRanapBaruLama && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-rasio-ranap" data-pdf-keep>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">{t.laporan.rasio_baru_ranap}</h3>
            <span className="text-xs text-slate-500">{t.laporan.rasio_baru_ranap_desc}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="rounded-2xl bg-teal-50 border border-teal-100 p-4" data-testid="rasio-ranap-baru">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-teal-600 mb-1">
                <span className="h-2.5 w-2.5 rounded-full bg-teal-500" />
                {t.laporan.pasien_baru}
              </div>
              <div className="font-heading text-3xl font-bold text-teal-700 tabular-nums" data-testid="rasio-ranap-baru-pct">{baruRanapPct.toFixed(1)}%</div>
              <div className="text-sm text-slate-500 tabular-nums mt-0.5">{(totalRanapBaru || 0).toLocaleString("id-ID")} {t.laporan.pasien}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="rasio-ranap-lama">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                {t.laporan.pasien_lama}
              </div>
              <div className="font-heading text-3xl font-bold text-slate-700 tabular-nums" data-testid="rasio-ranap-lama-pct">{lamaRanapPct.toFixed(1)}%</div>
              <div className="text-sm text-slate-500 tabular-nums mt-0.5">{(totalRanapLama || 0).toLocaleString("id-ID")} {t.laporan.pasien}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.total_ranap}</div>
              <div className="font-heading text-3xl font-bold text-slate-800 tabular-nums">{(totalRanap || 0).toLocaleString("id-ID")}</div>
              <div className="text-sm text-slate-500 mt-0.5">{t.laporan.pasien}</div>
            </div>
          </div>
          {/* Stacked ratio bar */}
          <div className="h-4 w-full rounded-full overflow-hidden flex bg-slate-100" aria-hidden="true">
            <div className="h-full bg-teal-500" style={{ width: `${baruRanapPct}%` }} title={`${t.laporan.pasien_baru} ${baruRanapPct.toFixed(1)}%`} />
            <div className="h-full bg-slate-400" style={{ width: `${lamaRanapPct}%` }} title={`${t.laporan.pasien_lama} ${lamaRanapPct.toFixed(1)}%`} />
          </div>

          {/* Sorot bangsal dengan proporsi pasien baru tertinggi */}
          {topBaruRanap && (
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="analisis-kunjungan-top-bangsal">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold px-2.5 py-1">
                <Star className="h-3.5 w-3.5" /> {t.laporan.top_poli_badge}
              </span>
              <div className="min-w-0">
                <div className="text-sm text-slate-600">{t.laporan.top_bangsal_label}</div>
                <div className="font-heading text-base font-bold text-slate-900 truncate" data-testid="analisis-kunjungan-top-bangsal-name">
                  {topBaruRanap.name} — <span className="text-amber-700 tabular-nums">{topBaruRanap.pct.toFixed(1)}%</span> {t.laporan.pasien_baru.toLowerCase()}
                </div>
              </div>
              <div className="ml-auto text-right text-sm text-slate-500 tabular-nums">
                {(topBaruRanap.baru || 0).toLocaleString("id-ID")} / {(topBaruRanap.visits || 0).toLocaleString("id-ID")} {t.laporan.pasien}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tren % pasien baru (pemantauan pertumbuhan) */}
      {showTrendBaru && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-trend-baru" data-pdf-keep>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4" data-testid="analisis-kunjungan-trend-baru-title">{t.laporan.trend_baru_title}{periodeRangeLabel(trendBaru) ? ` (${periodeRangeLabel(trendBaru)})` : ""}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendBaru} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="baruGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="periode" fontSize={11} />
                <YAxis fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => [`${v}%`, t.laporan.pasien_baru]} />
                <Area type="monotone" dataKey="pct" name={`% ${t.laporan.pasien_baru}`} stroke="#059669" strokeWidth={2.5} fill="url(#baruGrad)" dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Trend line */}
      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-trend" data-pdf-keep>
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4" data-testid="analisis-kunjungan-trend-title">
          {lang === "en" ? "Trend" : "Tren"} {t.laporan.total_visits}
          {periodeRangeLabel(trend) ? ` (${periodeRangeLabel(trend)})` : ""}
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="periode" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="Total" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Rawat Jalan breakdown (per poli) — dengan pasien Baru & Lama */}
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-poli" data-pdf-keep>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">
              {breakdownTitle || (hasRanap ? t.laporan.breakdown_ralan : t.laporan.breakdown_poli)}
            </h3>
            {hasBaruLama && (
              <div className="flex items-center gap-3 text-xs text-slate-600" data-testid="analisis-kunjungan-barulama-legend">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />{t.laporan.pasien_baru}: <b className="tabular-nums text-slate-800">{(totalBaru || 0).toLocaleString("id-ID")}</b></span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-indigo-500" />{t.laporan.pasien_lama}: <b className="tabular-nums text-slate-800">{(totalLama || 0).toLocaleString("id-ID")}</b></span>
              </div>
            )}
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={poli} margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="name" fontSize={11} width={120} />
                <Tooltip />
                {hasBaruLama ? (
                  <>
                    <Bar dataKey="baru" stackId="rj" name={t.laporan.pasien_baru} fill="#10b981" />
                    <Bar dataKey="lama" stackId="rj" name={t.laporan.pasien_lama} fill="#4f46e5" radius={[0, 6, 6, 0]} />
                  </>
                ) : (
                  <Bar dataKey="visits" fill="#4f46e5" radius={[0, 6, 6, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {hasRanap ? (
          /* Rawat Inap breakdown (per bangsal) */
          <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-ranap" data-pdf-keep>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {t.laporan.breakdown_ranap}
              </h3>
              {hasRanapBaruLama && (
                <div className="flex items-center gap-3 text-xs text-slate-600" data-testid="analisis-kunjungan-ranap-barulama-legend">
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-teal-500" />{t.laporan.pasien_baru}: <b className="tabular-nums text-slate-800">{(totalRanapBaru || 0).toLocaleString("id-ID")}</b></span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" />{t.laporan.pasien_lama}: <b className="tabular-nums text-slate-800">{(totalRanapLama || 0).toLocaleString("id-ID")}</b></span>
                </div>
              )}
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={ranap} margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="name" fontSize={11} width={120} />
                  <Tooltip />
                  {hasRanapBaruLama ? (
                    <>
                      <Bar dataKey="baru" stackId="ri" name={t.laporan.pasien_baru} fill="#0d9488" />
                      <Bar dataKey="lama" stackId="ri" name={t.laporan.pasien_lama} fill="#94a3b8" radius={[0, 6, 6, 0]} />
                    </>
                  ) : (
                    <Bar dataKey="visits" fill="#0d9488" radius={[0, 6, 6, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          /* Weekday pattern */
          <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-weekday" data-pdf-keep>
            <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
              {t.laporan.weekday_pattern}
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekday} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="visits" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Tren hunian Rawat Inap per bangsal */}
      {showRanapTrend && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-ranap-trend" data-pdf-keep>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4" data-testid="analisis-kunjungan-ranap-trend-title">{t.laporan.trend_ranap_title}{ranapSeries && periodeRangeLabel(ranapSeries.data) ? ` (${periodeRangeLabel(ranapSeries.data)})` : ""}</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ranapSeries.data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="periode" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {ranapSeries.names.map((n, i) => (
                  <Line key={n} type="monotone" dataKey={n} stroke={RANAP_COLORS[i % RANAP_COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Weekday pattern (full width when both breakdowns are shown) */}
      {hasRanap && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-weekday" data-pdf-keep>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
            {t.laporan.weekday_pattern}
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekday} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="visits" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabel rincian Rawat Jalan per poliklinik (Baru & Lama) */}
      {hasBaruLama && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="analisis-kunjungan-table" data-pdf-keep>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">{t.laporan.ralan_table_title}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2.5 pr-3 font-semibold">{t.laporan.col_poli}</th>
                  <th className="py-2.5 px-3 font-semibold text-right">{t.laporan.pasien_baru}</th>
                  <th className="py-2.5 px-3 font-semibold text-right">{t.laporan.pasien_lama}</th>
                  <th className="py-2.5 px-3 font-semibold text-right">{t.laporan.total_visits}</th>
                  <th className="py-2.5 pl-3 font-semibold text-right">% {t.laporan.pasien_baru}</th>
                </tr>
              </thead>
              <tbody>
                {ralan.map((row, i) => {
                  const v = row.visits || 0;
                  const b = row.baru || 0;
                  const pct = v > 0 ? (b / v) * 100 : 0;
                  const isTop = topBaruPoli && (row.code || row.name) === (topBaruPoli.code || topBaruPoli.name);
                  return (
                    <tr key={row.code || i} className={`border-b border-slate-100 ${isTop ? "bg-amber-50" : "hover:bg-slate-50/70"}`} data-testid={`ralan-table-row-${i}`}>
                      <td className="py-2.5 pr-3 text-slate-800">
                        <span className="inline-flex items-center gap-1.5">
                          {isTop && <Star className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                          {row.name}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700 font-medium">{b.toLocaleString("id-ID")}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-indigo-700 font-medium">{(row.lama || 0).toLocaleString("id-ID")}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-slate-900">{v.toLocaleString("id-ID")}</td>
                      <td className={`py-2.5 pl-3 text-right tabular-nums ${isTop ? "text-amber-700 font-semibold" : "text-slate-600"}`}>{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-bold text-slate-900" data-testid="ralan-table-total">
                  <td className="py-2.5 pr-3">{t.laporan.total_ralan}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700">{(totalBaru || 0).toLocaleString("id-ID")}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-indigo-700">{(totalLama || 0).toLocaleString("id-ID")}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{(totalRalan || 0).toLocaleString("id-ID")}</td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{baruPct.toFixed(1)}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// APOTEK view — resep (visits) + rekapitulasi penggunaan obat
// =====================================================================
const ApotekView = ({ entry, all, t, lang }) => {
  const d = entry?.data || {};
  const obat = d.obat || {};
  const topObat = obat.top_obat || [];
  return (
    <div className="space-y-6" data-testid="apotek-view">
      {/* Reuse the visits view for prescriptions */}
      <KunjunganAnalisisView
        entry={entry}
        all={all}
        t={t}
        lang={lang}
        breakdownTitle={t.laporan.apotek_resep_breakdown}
      />

      {/* Rekapitulasi Penggunaan Obat */}
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-rose-400 to-pink-500" />
          <h2 className="font-heading text-xl font-bold text-slate-900">{t.laporan.apotek_obat_title}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-pdf-keep>
          <div className="bg-white rounded-2xl border border-slate-100 p-5 col-span-2 md:col-span-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.apotek_obat_total}</div>
            <div className="font-heading text-3xl font-bold tabular-nums text-rose-600">
              {(obat.total_item || 0).toLocaleString("id-ID")}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5 col-span-2 md:col-span-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.laporan.apotek_resep_total}</div>
            <div className="font-heading text-3xl font-bold tabular-nums text-slate-900">
              {(d.total_resep || d.total || 0).toLocaleString("id-ID")}
            </div>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="apotek-top-obat" data-pdf-keep>
            <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">{t.laporan.apotek_obat_top}</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topObat} margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="name" fontSize={11} width={150} />
                  <Tooltip />
                  <Bar dataKey="qty" fill="#e11d48" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <DonutCard title={t.laporan.apotek_obat_kategori} data={obat.by_kategori || []} testid="apotek-kategori" />
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// BOR view
// =====================================================================
const BORView = ({ entry, all, t, lang }) => {
  const d = entry?.data || {};
  const targets = d.targets || {};

  // Status arah terhadap rentang target: in (ideal) / below / above.
  const indStatus = (key, val) => {
    const tg = targets[key];
    if (!tg || val == null) return { met: true, dir: "in" };
    if (val < tg.min) return { met: false, dir: "below" };
    if (val > tg.max) return { met: false, dir: "above" };
    return { met: true, dir: "in" };
  };

  const indicators = [
    { key: "bor", label: "BOR", value: d.bor, unit: targets.bor?.unit || "%", desc: t.laporan.ind_bor_desc },
    { key: "los", label: "LOS", value: d.los, unit: targets.los?.unit || "hari", desc: t.laporan.ind_los_desc },
    { key: "toi", label: "TOI", value: d.toi, unit: targets.toi?.unit || "hari", desc: t.laporan.ind_toi_desc },
    { key: "bto", label: "BTO", value: d.bto, unit: targets.bto?.unit || "kali", desc: t.laporan.ind_bto_desc },
  ];

  // Kartu Mutu — Angka Kematian (NDR/GDR), dihitung dari data BOR bulan ini.
  // NDR = kematian >= 48 jam / pasien keluar (hidup+mati) x 1000 (per mille), target Kemenkes <= 25.
  // GDR = total kematian / pasien keluar (hidup+mati) x 1000 (per mille), target Kemenkes <= 45.
  const deathsLt = Number(d.deaths_lt48h || 0);
  const deathsGt = Number(d.deaths_gt48h || 0);
  const totalDeaths = deathsLt + deathsGt;
  const dischargesN = Number(d.discharges || 0);
  const ndr = dischargesN > 0 ? (deathsGt / dischargesN) * 1000 : null;
  const gdr = dischargesN > 0 ? (totalDeaths / dischargesN) * 1000 : null;
  const mutu = [
    { key: "ndr", label: t.laporan.mutu_ndr, desc: t.laporan.mutu_ndr_desc, value: ndr, target: 25 },
    { key: "gdr", label: t.laporan.mutu_gdr, desc: t.laporan.mutu_gdr_desc, value: gdr, target: 45 },
  ];

  const trend = [...(all || [])]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      return {
        periode: `${mn} ${String(x.year).slice(-2)}`,
        BOR: x.data?.bor || 0,
        LOS: x.data?.los || 0,
        TOI: x.data?.toi || 0,
        BTO: x.data?.bto || 0,
      };
    });

  // Rentang periode dinamis untuk judul grafik tren (ikuti data aktual, bukan hardcoded)
  const trendRange = periodeRangeLabel(trend);

  // BOR per bangsal — snapshot bulan berjalan (untuk sorot bangsal terpadat)
  const borBangsalNow = Array.isArray(d.bor_per_bangsal) ? d.bor_per_bangsal : [];
  const topBangsal = borBangsalNow.length > 0
    ? borBangsalNow.reduce((best, x) => (!best || (x.bor || 0) > (best.bor || 0) ? x : best), null)
    : null;

  // Tren BOR% per bangsal (multi-garis, beberapa bulan terakhir)
  const BANGSAL_COLORS = ["#ef4444", "#0d9488", "#4f46e5", "#f59e0b", "#8b5cf6", "#0ea5e9"];
  const borBangsalSeries = (() => {
    const months = [...(all || [])]
      .sort((a, b) => a.year - b.year || a.month - b.month)
      .filter((x) => Array.isArray(x.data?.bor_per_bangsal) && x.data.bor_per_bangsal.length > 0);
    if (months.length < 2) return null;
    const agg = {};
    months.forEach((x) => x.data.bor_per_bangsal.forEach((b) => { agg[b.name] = (agg[b.name] || 0) + (b.bor || 0); }));
    const topNames = Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 6).map((e) => e[0]);
    const data = months.map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      const map = Object.fromEntries(x.data.bor_per_bangsal.map((b) => [b.name, b.bor || 0]));
      const row = { periode: `${mn} ${String(x.year).slice(-2)}` };
      topNames.forEach((n) => { row[n] = map[n] ?? null; });
      return row;
    });
    return { data, names: topNames };
  })();
  const showBorBangsal = borBangsalNow.length > 0;

  return (
    <div className="space-y-6">
      {/* Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="bor-indicators">
        {indicators.map((ind) => {
          const st = indStatus(ind.key, ind.value);
          const tg = targets[ind.key];
          // Pembeda profesional 3 status: ideal / di atas target / di bawah target.
          // Ikon + warna berbeda agar arah simpangan langsung jelas.
          const styleByDir = {
            in: { bar: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2, label: t.laporan.status_met },
            above: { bar: "bg-rose-500", badge: "bg-rose-50 text-rose-700", Icon: TrendingUp, label: t.laporan.status_above },
            below: { bar: "bg-amber-500", badge: "bg-amber-50 text-amber-700", Icon: TrendingDown, label: t.laporan.status_under },
          };
          const sv = styleByDir[st.dir] || styleByDir.in;
          const StatusIcon = sv.Icon;
          return (
            <div key={ind.key} className="bg-white rounded-2xl border border-slate-100 p-5 relative overflow-hidden">
              <div className={`absolute inset-x-0 top-0 h-1 ${sv.bar}`} />
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{ind.label}</div>
              <div className="font-heading text-3xl font-bold text-slate-900 tabular-nums">
                {ind.value}
                <span className="text-sm font-normal text-slate-400 ml-1">{ind.unit}</span>
              </div>
              {ind.desc && (
                <div className="text-[11px] text-slate-500 mt-1 leading-snug" data-testid={`bor-ind-desc-${ind.key}`}>
                  {ind.desc}
                </div>
              )}
              {tg && (
                <div className="text-xs text-slate-400 mt-1">
                  {t.laporan.target_range}: {tg.min}–{tg.max} {tg.unit}
                </div>
              )}
              <span
                data-testid={`bor-ind-status-${ind.key}`}
                data-status-dir={st.dir}
                className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sv.badge}`}
              >
                <StatusIcon className="h-3 w-3" />
                {sv.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Kartu Mutu — Angka Kematian (NDR/GDR) */}
      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="bor-mutu-card" data-pdf-keep>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-rose-500" />
          <h3 className="font-heading text-lg font-bold text-slate-900">{t.laporan.mutu_title}</h3>
        </div>
        {dischargesN > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="bor-mutu-grid">
            {mutu.map((m) => {
              const met = m.value != null && m.value <= m.target;
              return (
                <div
                  key={m.key}
                  data-testid={`bor-mutu-${m.key}`}
                  className="rounded-2xl border border-slate-100 p-5 relative overflow-hidden"
                >
                  <div className={`absolute inset-x-0 top-0 h-1 ${met ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{m.label}</div>
                  <div className="font-heading text-3xl font-bold text-slate-900 tabular-nums">
                    {m.value != null ? m.value.toFixed(2) : "—"}
                    <span className="text-sm font-normal text-slate-400 ml-1">‰</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{m.desc}</div>
                  <div className="text-xs text-slate-400">{t.laporan.mutu_target}: ≤ {m.target} ‰</div>
                  <span
                    className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      met ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                    }`}
                  >
                    {met ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                    {met ? t.laporan.status_met : t.laporan.status_below}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-slate-500" data-testid="bor-mutu-empty">
            {t.laporan.mutu_no_data}
          </div>
        )}
        <div className="text-xs text-slate-400 mt-3">
          {t.laporan.mutu_deaths_total}: <span className="font-semibold text-slate-600 tabular-nums">{totalDeaths}</span>
          {" "}(&lt; 48h: {deathsLt} · ≥ 48h: {deathsGt}) · {t.laporan.discharges}: <span className="font-semibold text-slate-600 tabular-nums">{dischargesN}</span>
        </div>
      </div>

      {/* Trend chart */}
      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="bor-trend" data-pdf-keep>
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4" data-testid="bor-trend-title">
          {lang === "en" ? "BOR Trend" : "Tren BOR"}{trendRange ? ` (${trendRange})` : ""}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="periode" fontSize={11} />
              <YAxis fontSize={11} domain={[0, 100]} />
              <Tooltip />
              <Legend />
              {targets.bor && (
                <ReferenceArea y1={targets.bor.min} y2={targets.bor.max} fill="#10b981" fillOpacity={0.08} />
              )}
              <Line type="monotone" dataKey="BOR" stroke="#f97316" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="text-xs text-slate-500 mt-2">
          Area hijau = rentang target Kemenkes ({targets.bor?.min}–{targets.bor?.max}%)
        </div>
      </div>

      {/* BOR per bangsal — sorot terpadat + snapshot + tren */}
      {showBorBangsal && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="bor-bangsal-card" data-pdf-keep>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">{t.laporan.bor_bangsal_title}</h3>
            {topBangsal && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold px-2.5 py-1" data-testid="bor-bangsal-top">
                <Star className="h-3.5 w-3.5" /> {t.laporan.bor_bangsal_top}: {topBangsal.name} — <span className="tabular-nums">{(topBangsal.bor || 0).toFixed(1)}%</span>
              </span>
            )}
          </div>

          {/* Snapshot bulan berjalan: BOR per bangsal */}
          <div className="h-72" data-testid="bor-bangsal-snapshot">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={borBangsalNow} margin={{ top: 5, right: 24, left: 30, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" fontSize={11} width={130} />
                <Tooltip formatter={(v) => [`${v}%`, "BOR"]} />
                {targets.bor && (
                  <ReferenceArea x1={targets.bor.min} x2={targets.bor.max} fill="#10b981" fillOpacity={0.08} />
                )}
                <Bar dataKey="bor" radius={[0, 6, 6, 0]}>
                  {borBangsalNow.map((b, i) => {
                    const over = targets.bor && b.bor > targets.bor.max;
                    const under = targets.bor && b.bor < targets.bor.min;
                    const color = over ? "#ef4444" : under ? "#f59e0b" : "#10b981";
                    return <Cell key={b.code || i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {t.laporan.bor_bangsal_hint} ({targets.bor?.min}–{targets.bor?.max}%)
          </div>

          {/* Tren BOR% per bangsal beberapa bulan terakhir */}
          {borBangsalSeries && (
            <div className="mt-6" data-testid="bor-bangsal-trend">
              <h4 className="font-heading text-base font-bold text-slate-800 mb-3" data-testid="bor-bangsal-trend-title">{t.laporan.bor_bangsal_trend_title}{periodeRangeLabel(borBangsalSeries.data) ? ` (${periodeRangeLabel(borBangsalSeries.data)})` : ""}</h4>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={borBangsalSeries.data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="periode" fontSize={11} />
                    <YAxis fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => [`${v}%`, ""]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {borBangsalSeries.names.map((n, i) => (
                      <Line key={n} type="monotone" dataKey={n} stroke={BANGSAL_COLORS[i % BANGSAL_COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail table */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
            Data Bulan Ini
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="text-slate-500">{t.laporan.beds_total}</div>
            <div className="text-right font-semibold text-slate-900 tabular-nums">{d.beds_total || 0}</div>
            <div className="text-slate-500">{t.laporan.admissions}</div>
            <div className="text-right font-semibold text-slate-900 tabular-nums">{d.admissions || 0}</div>
            <div className="text-slate-500">{t.laporan.discharges}</div>
            <div className="text-right font-semibold text-slate-900 tabular-nums">{d.discharges || 0}</div>
            <div className="text-slate-500">{t.laporan.deaths} &lt; 48h</div>
            <div className="text-right font-semibold text-slate-900 tabular-nums">{d.deaths_lt48h || 0}</div>
            <div className="text-slate-500">{t.laporan.deaths} ≥ 48h</div>
            <div className="text-right font-semibold text-slate-900 tabular-nums">{d.deaths_gt48h || 0}</div>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
            Rangkuman Indikator
          </h3>
          <div className="space-y-2 text-sm">
            {indicators.map((ind) => {
              const st = indStatus(ind.key, ind.value);
              const label = st.met
                ? t.laporan.status_met
                : st.dir === "above"
                ? t.laporan.status_above
                : t.laporan.status_under;
              return (
                <div key={ind.key} className="flex items-start justify-between gap-3">
                  <span className="text-slate-500">
                    <span className="font-semibold text-slate-800">{ind.label}</span> — {ind.value} {ind.unit}
                    {ind.desc && <span className="block text-[11px] text-slate-400">{ind.desc}</span>}
                  </span>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    st.met
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-700"
                  }`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// PEGAWAI (Employee recap) view
// =====================================================================
const PEGAWAI_COLORS = ["#4f46e5", "#0891b2", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

const DonutCard = ({ title, data, testid }) => {
  const total = (data || []).reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid={testid} data-pdf-keep>
      <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={90}
              paddingAngle={2}
            >
              {(data || []).map((_, i) => (
                <Cell key={i} fill={PEGAWAI_COLORS[i % PEGAWAI_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} (${total ? Math.round((v / total) * 100) : 0}%)`, n]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const PegawaiView = ({ entry, all, t, lang }) => {
  const d = entry?.data || {};
  const byStatus = d.by_status || [];
  const byType = d.by_type || [];
  const byUnit = d.by_unit || [];
  const byGender = d.by_gender || [];
  const trend = [...(all || [])]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((x) => {
      const mn = (lang === "en" ? ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] : ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"])[x.month];
      return { periode: `${mn} ${String(x.year).slice(-2)}`, Total: x.data?.total || 0 };
    });

  const kpis = [
    { label: t.laporan.siperkasa_pegawai_total, value: d.total || 0, color: "text-slate-900" },
    { label: t.nav.analisis_pegawai && byType[0]?.name, value: byType[0]?.value || 0, color: "text-indigo-600" },
    { label: byType[1]?.name, value: byType[1]?.value || 0, color: "text-cyan-600" },
    { label: byType[3]?.name, value: byType[3]?.value || 0, color: "text-emerald-600" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pegawai-kpis" data-pdf-keep>
        {kpis.map((k, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 min-h-[2rem]">{k.label}</div>
            <div className={`font-heading text-3xl font-bold tabular-nums ${k.color}`}>
              {(k.value || 0).toLocaleString("id-ID")}
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <DonutCard title={t.laporan.siperkasa_by_status} data={byStatus} testid="pegawai-status" />
        <DonutCard title={t.laporan.siperkasa_by_type} data={byType} testid="pegawai-type" />
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="pegawai-unit" data-pdf-keep>
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">{t.laporan.siperkasa_by_unit}</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={byUnit} margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" fontSize={11} />
              <YAxis type="category" dataKey="name" fontSize={11} width={200} />
              <Tooltip />
              <Bar dataKey="value" fill="#10b981" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <DonutCard title={t.laporan.siperkasa_by_gender} data={byGender} testid="pegawai-gender" />
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="pegawai-trend" data-pdf-keep>
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4" data-testid="pegawai-trend-title">{t.laporan.siperkasa_headcount_trend}{periodeRangeLabel(trend) ? ` (${periodeRangeLabel(trend)})` : ""}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="periode" fontSize={11} />
                <YAxis fontSize={11} domain={["dataMin - 10", "dataMax + 5"]} />
                <Tooltip />
                <Line type="monotone" dataKey="Total" stroke="#059669" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// MAIN Analisis Detail page
// =====================================================================

// Classify the raw SIRS `_source` string into an operator-friendly verdict.
// Three real states exist:
//   SIRS-SIMRS-MARIADB   -> live data pulled from the real SIMRS database
//   SIRS-PHP-API         -> reachable PHP demo server (NOT the real DB)
//   ...-FALLBACK         -> internal Python fallback (SIRS unreachable)
const classifySource = (source, lang, dbConnected) => {
  const s = String(source || "");
  if (s.includes("-FALLBACK")) {
    return {
      key: "fallback",
      live: false,
      label: lang === "en" ? "Backup (Fallback)" : "Cadangan (Fallback)",
      tip:
        lang === "en"
          ? "SIRS is unreachable — showing internal backup data."
          : "SIRS tidak terjangkau — menampilkan data cadangan internal.",
      cls: "bg-amber-50 border-amber-200 text-amber-800",
      Icon: CloudOff,
    };
  }
  if (s === "SIRS-SIMRS-MARIADB") {
    // The bridge is reachable, but if its database is NOT connected it serves
    // built-in DEMO numbers — so we must NOT flag it as live in that case.
    if (dbConnected === false) {
      return {
        key: "demo",
        live: false,
        label: lang === "en" ? "SIRS (Demo — DB off)" : "SIRS (Demo — DB terputus)",
        tip:
          lang === "en"
            ? "Bridge reachable but not connected to the SIMRS database — figures are demo data."
            : "Bridge aktif tetapi belum tersambung ke database SIMRS — angka adalah data demo.",
        cls: "bg-rose-50 border-rose-200 text-rose-800",
        Icon: AlertTriangle,
      };
    }
    return {
      key: "live",
      live: true,
      label: lang === "en" ? "Live SIRS" : "SIRS Asli",
      tip:
        lang === "en"
          ? "Data pulled live from the hospital SIMRS database."
          : "Data ditarik langsung dari database SIMRS rumah sakit.",
      cls: "bg-emerald-50 border-emerald-200 text-emerald-800",
      Icon: ShieldCheck,
    };
  }
  if (s === "SIRS-PHP-API") {
    return {
      key: "demo",
      live: false,
      label: lang === "en" ? "SIRS (Demo)" : "SIRS (Demo)",
      tip:
        lang === "en"
          ? "Connected to the SIRS demo server (not the real database)."
          : "Terhubung ke server SIRS demo (bukan database asli).",
      cls: "bg-sky-50 border-sky-200 text-sky-800",
      Icon: Database,
    };
  }
  return {
    key: "unknown",
    live: false,
    label: lang === "en" ? "Unknown source" : "Sumber tidak diketahui",
    tip: lang === "en" ? "Data source not recognized." : "Sumber data tidak dikenali.",
    cls: "bg-slate-50 border-slate-200 text-slate-600",
    Icon: HelpCircle,
  };
};

// Small operator-facing badge shown on every SIPERKASA data page so users
// immediately know whether the figures are live (from SIRS) or backup.
const DataSourceBadge = ({ source, lang, dbConnected }) => {
  if (!source) return null;
  const v = classifySource(source, lang, dbConnected);
  const Icon = v.Icon;
  return (
    <div
      data-testid="siperkasa-source-badge"
      data-source-key={v.key}
      title={v.tip}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${v.cls}`}
    >
      {v.live ? (
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
      ) : (
        <Icon className="h-3.5 w-3.5" />
      )}
      <span>
        {lang === "en" ? "Source" : "Sumber"}: {v.label}
      </span>
    </div>
  );
};

// Prominent, operator-facing warning shown on SIPERKASA pages whenever the
// figures are NOT real hospital data — either the SIRS bridge is unreachable
// (fallback) or it is reachable but its SIMRS database is not connected (demo).
const SourceWarningBanner = ({ verdict, lang }) => {
  if (!verdict || verdict.mode === "live") return null;
  const isDemo = verdict.mode === "demo";
  const isFallback = verdict.mode === "fallback";
  if (!isDemo && !isFallback) return null; // "unknown" → stay silent
  const title = isDemo
    ? lang === "en"
      ? "Showing DEMO data — not real hospital figures"
      : "Menampilkan DATA DEMO — bukan angka rumah sakit sebenarnya"
    : lang === "en"
    ? "SIRS unreachable — showing internal backup data"
    : "SIRS tidak terjangkau — menampilkan data cadangan internal";
  const body = isDemo
    ? lang === "en"
      ? "The SIRS bridge is running but not connected to the SIMRS database (db_connected: false), so these numbers are placeholders. Ask the admin to configure the database connection to display real data."
      : "Bridge SIRS aktif tetapi belum tersambung ke database SIMRS (db_connected: false), sehingga angka ini hanya contoh. Hubungi admin untuk mengonfigurasi koneksi database agar data asli tampil."
    : lang === "en"
    ? "The hospital SIRS/SIMRS could not be reached, so internal backup figures are shown. They do not reflect the live database."
    : "SIRS/SIMRS rumah sakit tidak dapat dijangkau, sehingga yang tampil adalah angka cadangan internal. Angka ini tidak mencerminkan database asli.";
  return (
    <div
      data-testid="siperkasa-source-warning"
      data-source-mode={verdict.mode}
      className={`mb-6 rounded-2xl border p-4 flex items-start gap-3 ${
        isDemo
          ? "bg-rose-50 border-rose-200"
          : "bg-amber-50 border-amber-200"
      }`}
    >
      <div
        className={`shrink-0 flex h-9 w-9 items-center justify-center rounded-xl ${
          isDemo ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"
        }`}
      >
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p
          className={`font-heading font-bold text-sm ${
            isDemo ? "text-rose-800" : "text-amber-800"
          }`}
        >
          {title}
        </p>
        <p
          className={`mt-1 text-xs leading-relaxed ${
            isDemo ? "text-rose-700/90" : "text-amber-700/90"
          }`}
        >
          {body}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// Year-vs-Year comparison (trend chart) for any SIPERKASA source
// ---------------------------------------------------------------
const MONTH_ABBR_ID = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
const MONTH_ABBR_EN = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Pull the single headline metric out of an entry for the given sub.
const extractValue = (sub, entry) => {
  const d = (entry && entry.data) || {};
  if (sub === "bor") return d.bor;
  if (sub === "apotek") return d.total_resep ?? d.total;
  // kunjungan, lab, radiologi, pegawai
  return d.total ?? (d.yoy && d.yoy.this_year);
};

// Merge two years' monthly lists into one dataset keyed by month.
const buildComparisonData = (sub, itemsA, itemsB, lang) => {
  const abbr = lang === "en" ? MONTH_ABBR_EN : MONTH_ABBR_ID;
  const mapA = {};
  const mapB = {};
  (itemsA || []).forEach((e) => { mapA[e.month] = extractValue(sub, e); });
  (itemsB || []).forEach((e) => { mapB[e.month] = extractValue(sub, e); });
  const months = Array.from(
    new Set([...Object.keys(mapA), ...Object.keys(mapB)].map(Number))
  ).sort((a, b) => a - b);
  return months.map((m) => ({
    month: m,
    label: abbr[m] || String(m),
    a: typeof mapA[m] === "number" ? mapA[m] : null,
    b: typeof mapB[m] === "number" ? mapB[m] : null,
  }));
};

const YearComparisonCard = ({ sub, itemsA, yearA, itemsB, yearB, lang }) => {
  const meta = KPI_META[sub] || {};
  const colorA = meta.color || "#2563eb";
  const colorB = "#f59e0b"; // amber — contrasts with every source color
  const isPercent = meta.suffix === "%";
  const data = useMemo(
    () => buildComparisonData(sub, itemsA, itemsB, lang),
    [sub, itemsA, itemsB, lang]
  );

  // Summary: sum for counts, average for percentage metrics (BOR).
  const agg = (items) => {
    const vals = (items || [])
      .map((e) => extractValue(sub, e))
      .filter((v) => typeof v === "number");
    if (!vals.length) return null;
    if (isPercent) return vals.reduce((s, v) => s + v, 0) / vals.length;
    return vals.reduce((s, v) => s + v, 0);
  };
  const aggA = agg(itemsA);
  const aggB = agg(itemsB);
  const delta =
    typeof aggA === "number" && typeof aggB === "number" && aggB !== 0
      ? ((aggA - aggB) / Math.abs(aggB)) * 100
      : null;

  const fmt = (n) =>
    typeof n === "number"
      ? (isPercent ? n.toFixed(1) : Math.round(n).toLocaleString(lang === "en" ? "en-US" : "id-ID"))
      : "—";

  const aggLabel = isPercent
    ? (lang === "en" ? "Average" : "Rata-rata")
    : (lang === "en" ? "Total" : "Total");

  return (
    <div
      data-testid="analisis-year-compare-card"
      className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-900">
            {lang === "en" ? "Year-over-year comparison" : "Perbandingan Antar Tahun"}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {lang === "en"
              ? `Monthly trend ${yearB} vs ${yearA}`
              : `Tren bulanan ${yearB} vs ${yearA}`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right" data-testid="analisis-compare-agg-a">
            <div className="flex items-center gap-1.5 justify-end text-xs font-medium" style={{ color: colorA }}>
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorA }}></span>
              {yearA}
            </div>
            <div className="text-lg font-bold text-slate-900">
              {fmt(aggA)}{isPercent ? "%" : ""}
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">{aggLabel}</div>
          </div>
          <div className="text-right" data-testid="analisis-compare-agg-b">
            <div className="flex items-center gap-1.5 justify-end text-xs font-medium" style={{ color: colorB }}>
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorB }}></span>
              {yearB}
            </div>
            <div className="text-lg font-bold text-slate-900">
              {fmt(aggB)}{isPercent ? "%" : ""}
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">{aggLabel}</div>
          </div>
          {typeof delta === "number" && (
            <div
              data-testid="analisis-compare-delta"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-sm font-semibold ${
                delta >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
              }`}
              title={`${yearA} vs ${yearB}`}
            >
              {delta >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
            </div>
          )}
        </div>
      </div>
      <div style={{ width: "100%", height: 300 }} data-pdf-keep data-testid="analisis-compare-chart">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={48} />
            <Tooltip
              formatter={(value, name) => [
                `${fmt(value)}${isPercent ? "%" : ""}`,
                name,
              ]}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="b"
              name={String(yearB)}
              stroke={colorB}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="a"
              name={String(yearA)}
              stroke={colorA}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Ringkasan selisih per bulan (tahun A vs tahun B) */}
      {data.length > 0 && (
        <div className="mt-6" data-pdf-keep data-testid="analisis-compare-monthly">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">
            {lang === "en" ? "Monthly difference" : "Selisih per bulan"}{" "}
            <span className="text-slate-400 font-normal">
              ({yearA} vs {yearB})
            </span>
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {data.map((row) => {
              const hasBoth =
                typeof row.a === "number" && typeof row.b === "number";
              const diff = hasBoth ? row.a - row.b : null;
              const pct =
                hasBoth && row.b !== 0 ? (diff / Math.abs(row.b)) * 100 : null;
              const up = typeof diff === "number" && diff >= 0;
              return (
                <div
                  key={row.month}
                  data-testid={`analisis-compare-month-${row.month}`}
                  className="rounded-2xl border border-slate-100 bg-slate-50/60 px-3 py-2.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">
                      {row.label}
                    </span>
                    {typeof diff === "number" ? (
                      <span
                        className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${
                          up ? "text-emerald-600" : "text-rose-600"
                        }`}
                      >
                        {up ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {up ? "+" : ""}
                        {isPercent
                          ? `${diff.toFixed(1)} pts`
                          : Math.round(diff).toLocaleString(
                              lang === "en" ? "en-US" : "id-ID"
                            )}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span
                        className="flex items-center gap-1"
                        style={{ color: colorA }}
                      >
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: colorA }}
                        ></span>
                        {yearA}
                      </span>
                      <span className="font-medium text-slate-700">
                        {fmt(row.a)}
                        {isPercent && typeof row.a === "number" ? "%" : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span
                        className="flex items-center gap-1"
                        style={{ color: colorB }}
                      >
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: colorB }}
                        ></span>
                        {yearB}
                      </span>
                      <span className="font-medium text-slate-700">
                        {fmt(row.b)}
                        {isPercent && typeof row.b === "number" ? "%" : ""}
                      </span>
                    </div>
                    {typeof pct === "number" && (
                      <div className="text-right text-[10px] text-slate-400">
                        {up ? "+" : ""}
                        {pct.toFixed(1)}%
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// Informative empty-data banner shown when a period/year has no data in
// SIRS/SIMRS — gives clear guidance instead of a bare "no data" message.
const EmptyDataBanner = ({
  lang,
  title,
  lines = [],
  testid = "analisis-empty-banner",
}) => (
  <div
    data-testid={testid}
    className="rounded-3xl bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 p-8 text-center"
  >
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
      <Database className="h-7 w-7" />
    </div>
    <h3 className="font-heading text-lg font-bold text-slate-800">{title}</h3>
    {lines.length > 0 && (
      <ul className="mt-3 space-y-1.5 text-sm text-slate-500 max-w-md mx-auto text-left">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-2">
            <HelpCircle className="h-4 w-4 mt-0.5 shrink-0 text-blue-400" />
            <span>{l}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);


export const AnalisisDetail = () => {
  const { sub } = useParams();
  const { t, lang } = useLanguage();
  const { authAxios, user } = useAuth();
  const isSuperadmin = user && user.role === "superadmin";
  const cfg = ANALISIS_BY_SLUG[sub];
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sirsMeta, setSirsMeta] = useState(null);
  const [sourceVerdict, setSourceVerdict] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [year, setYear] = useState(SIPERKASA_CURRENT_YEAR);
  const [compareOn, setCompareOn] = useState(false);
  const [compareYear, setCompareYear] = useState(SIPERKASA_CURRENT_YEAR - 1);
  const [compareItems, setCompareItems] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const contentRef = useRef(null);

  // Fetch the normalized data-source verdict (live / demo / fallback) so we can
  // warn the operator when the figures are not real hospital data.
  useEffect(() => {
    let alive = true;
    authAxios
      .get("/sirs/data-source")
      .then((r) => {
        if (alive) setSourceVerdict(r.data);
      })
      .catch(() => {
        if (alive) setSourceVerdict(null);
      });
    return () => {
      alive = false;
    };
  }, [sub, authAxios]);

  const handleDownloadPdf = async () => {
    if (!contentRef.current) {
      toast.error(lang === "en" ? "Content not ready" : "Konten belum siap");
      return;
    }
    const periodLabel = selected
      ? formatPeriod(selected.year, selected.month, lang)
      : "";
    setDownloadingPdf(true);
    try {
      await exportReportToPdf({
        node: contentRef.current,
        avoidBreakSelector: "[data-pdf-keep]",
        filename: `analisis-${sub}${
          selected ? `-${selected.year}-${selected.month}` : ""
        }.pdf`,
        logoUrl: "/logo.jpg",
        headingLines: [
          "RS BHAYANGKARA TARAKAN",
          "Polda Kalimantan Utara",
          `${lang === "en" ? "Analysis" : "Analisis"}: ${
            t.laporan[cfg.key_title] || cfg.slug
          }`,
          periodLabel ? `${lang === "en" ? "Period" : "Periode"}: ${periodLabel}` : "",
        ],
      });
      toast.success(lang === "en" ? "PDF downloaded" : "PDF berhasil diunduh");
    } catch (e) {
      toast.error(lang === "en" ? "Failed to create PDF" : "Gagal membuat PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // One-time: default the year to the LATEST period that actually exists in
  // SIRS/SIMRS (instead of the current calendar year), so operators land on the
  // most recent available data. Never overrides a year the user later picks.
  const initedYearRef = useRef(false);
  useEffect(() => {
    if (initedYearRef.current) return;
    let alive = true;
    authAxios
      .get("/analisis/latest-period", { params: { sub } })
      .then((r) => {
        const ly = Number(r.data?.year);
        if (!alive) return;
        initedYearRef.current = true;
        if (Number.isFinite(ly) && ly > 0) {
          setYear(ly);
          setCompareYear(ly - 1);
        }
      })
      .catch(() => {
        if (alive) initedYearRef.current = true;
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!cfg) return;
    setLoading(true);
    setError(null);
    setSirsMeta(null);
    authAxios
      .get(`/analisis/${sub}`, { params: { year } })
      .then((r) => {
        const list = r.data || [];
        setItems(list);
        setSelected(list[0] || null);
        // Capture data-source metadata from the first entry
        if (list[0]?._source) {
          setSirsMeta({
            source: list[0]._source,
            version: list[0]._version,
            endpoint: list[0]._endpoint,
            fetched_at: list[0]._fetched_at,
          });
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, year]);

  // Fetch the comparison year's data on demand.
  useEffect(() => {
    if (!cfg || !compareOn) {
      setCompareItems([]);
      return;
    }
    let alive = true;
    setCompareLoading(true);
    authAxios
      .get(`/analisis/${sub}`, { params: { year: compareYear } })
      .then((r) => {
        if (alive) setCompareItems(r.data || []);
      })
      .catch(() => {
        if (alive) setCompareItems([]);
      })
      .finally(() => {
        if (alive) setCompareLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, compareOn, compareYear]);

  // When comparison is switched on (or the compared year changes), bring the
  // result into view so the operator immediately sees that the data was
  // processed — otherwise the comparison card renders far below the fold and
  // it looks like "nothing happened".
  useEffect(() => {
    if (!compareOn || compareLoading) return;
    const id = setTimeout(() => {
      const el = document.querySelector(
        '[data-testid="analisis-year-compare-card"], [data-testid="analisis-compare-empty"], [data-testid="analisis-compare-same-year"]'
      );
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
    return () => clearTimeout(id);
  }, [compareOn, compareYear, compareLoading, compareItems]);

  const periodOptions = useMemo(
    () =>
      items.map((x) => ({
        value: `${x.year}-${x.month}`,
        label: formatPeriod(x.year, x.month, lang),
        entry: x,
      })),
    [items, lang]
  );

  if (!cfg) return <Navigate to="/siperkasa" replace />;

  const pdfHref = buildPdfUrl(selected?.pdf_url);

  return (
    <AuthGate>
      <div className="min-h-screen flex flex-col bg-slate-50 print-report" data-testid={`analisis-page-${sub}`}>
        <Header />
        <main className="flex-1 pt-28 md:pt-32 pb-16">
          <div className="container-x">
            {/* Print header (kop) — hidden on screen, visible only when printing */}
            <div
              className="print-only mb-6"
              data-testid="analisis-print-header"
            >
              <div className="flex items-center gap-4 pb-4 border-b-2 border-slate-800">
                <img
                  src={HOSPITAL.logo}
                  alt="Logo RS Bhayangkara Tarakan"
                  className="h-16 w-16 rounded-full object-cover"
                  crossOrigin="anonymous"
                />
                <div className="flex-1">
                  <div className="font-heading font-bold text-lg text-slate-900">
                    RS BHAYANGKARA TARAKAN
                  </div>
                  <div className="text-xs text-slate-600">
                    {HOSPITAL.affiliation} — {HOSPITAL.address_id}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Telp: {HOSPITAL.phone_display} • Email: {HOSPITAL.email}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <div className="text-xs uppercase tracking-widest text-slate-500">
                  Laporan Analisis Data
                </div>
                <div className="font-heading font-bold text-xl text-slate-900 mt-1">
                  {t.laporan[cfg.key_title]}
                </div>
                {selected && (
                  <div className="text-sm text-slate-600 mt-1">
                    Periode: {formatPeriod(selected.year, selected.month, lang)}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4 flex items-center justify-between gap-3 no-print">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Link to="/" className="hover:text-blue-800">{t.nav.home}</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/siperkasa" className="hover:text-blue-800">{t.nav.siperkasa}</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-amber-700 font-medium">{t.laporan[cfg.key_title]}</span>
              </div>
              <div className="flex items-center gap-2">
                <LatestPeriodChip sub={sub} lang={lang} />
                <SiperkasaUserChip lang={lang} />
              </div>
            </div>

            <div className="no-print">
              <SirsDisruptionBanner lang={lang} />
              <SourceWarningBanner verdict={sourceVerdict} lang={lang} />
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 no-print">
              <div>
                <Link
                  to="/siperkasa"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-700 mb-3"
                >
                  <ArrowLeft className="h-3 w-3" />
                  {t.nav.siperkasa}
                </Link>
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
                  <Lock className="h-3 w-3" />
                  {t.nav.siperkasa}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
                  {t.laporan[cfg.key_title]}
                </h1>
                <p className="text-sm text-slate-500 mt-1">{t.laporan[cfg.key_desc]}</p>
                {sirsMeta && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <DataSourceBadge
                      source={sirsMeta.source}
                      lang={lang}
                      dbConnected={sourceVerdict?.db_connected}
                    />
                    {isSuperadmin && (
                      <div
                        data-testid="sirs-data-source-badge"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs"
                      >
                        <Server className="h-3 w-3 text-slate-500" />
                        <span className="text-slate-700 font-mono">{sirsMeta.source}</span>
                        <span className="text-slate-500">v{sirsMeta.version}</span>
                        <span className="text-slate-400 hidden sm:inline">
                          • {new Date(sirsMeta.fetched_at).toLocaleTimeString("id-ID")}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                    {lang === "en" ? "Year" : "Tahun"}
                  </label>
                  <div className="h-[46px] flex items-center">
                    <YearSelect
                      value={year}
                      lang={lang}
                      testid="analisis-year"
                      onChange={(y) => setYear(y)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                    {t.laporan.select_period}
                  </label>
                  <select
                    data-testid="analisis-period-select"
                    value={selected ? `${selected.year}-${selected.month}` : ""}
                    onChange={(e) => {
                      const opt = periodOptions.find((p) => p.value === e.target.value);
                      setSelected(opt?.entry || null);
                    }}
                    className="min-w-[180px] px-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  >
                    {periodOptions.length === 0 && <option value="">—</option>}
                    {periodOptions.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                    {lang === "en" ? "Compare" : "Bandingkan"}
                  </label>
                  <div className="h-[46px] flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="analisis-compare-toggle"
                      aria-pressed={compareOn}
                      onClick={() => setCompareOn((v) => !v)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${
                        compareOn
                          ? "bg-amber-600 border-amber-600 text-white shadow-md"
                          : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                      }`}
                    >
                      <TrendingUp className="h-4 w-4" />
                      {lang === "en" ? "Compare year" : "Bandingkan tahun"}
                    </button>
                    {compareOn && (
                      <YearSelect
                        value={compareYear}
                        lang={lang}
                        testid="analisis-compare-year"
                        onChange={(y) => setCompareYear(y)}
                      />
                    )}
                  </div>
                  {compareOn && (
                    <button
                      type="button"
                      data-testid="analisis-compare-jump"
                      onClick={() => {
                        const el = document.querySelector(
                          '[data-testid="analisis-year-compare-card"], [data-testid="analisis-compare-empty"], [data-testid="analisis-compare-same-year"]'
                        );
                        if (el && typeof el.scrollIntoView === "function")
                          el.scrollIntoView({ behavior: "smooth", block: "center" });
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-800"
                    >
                      {lang === "en" ? "See comparison below" : "Lihat hasil di bawah"} ↓
                    </button>
                  )}
                  {compareOn && (
                    <div
                      data-testid="analisis-compare-status"
                      className={`mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium ${
                        compareLoading
                          ? "text-slate-500"
                          : year === compareYear
                          ? "text-amber-700"
                          : compareItems.length === 0
                          ? "text-rose-600"
                          : "text-emerald-600"
                      }`}
                    >
                      {compareLoading ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {lang === "en"
                            ? `Processing ${compareYear} data…`
                            : `Memproses data ${compareYear}…`}
                        </>
                      ) : year === compareYear ? (
                        <>
                          <TrendingUp className="h-3 w-3" />
                          {lang === "en"
                            ? "Pick a different year"
                            : "Pilih tahun berbeda"}
                        </>
                      ) : compareItems.length === 0 ? (
                        <>
                          <FileText className="h-3 w-3" />
                          {lang === "en"
                            ? `No data for ${compareYear}`
                            : `Tidak ada data untuk ${compareYear}`}
                        </>
                      ) : (
                        <>
                          <TrendingUp className="h-3 w-3" />
                          {lang === "en"
                            ? `Showing ${compareYear} vs ${year}`
                            : `Menampilkan ${compareYear} vs ${year}`}
                        </>
                      )}
                    </div>
                  )}
                </div>
                {pdfHref ? (
                  <a
                    href={pdfHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="analisis-download-pdf"
                    className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md"
                  >
                    <Download className="h-4 w-4" />
                    {t.laporan.download_pdf}
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 bg-slate-100 text-slate-500 text-sm font-medium px-5 py-3 rounded-xl">
                    <FileText className="h-4 w-4" />
                    {t.laporan.no_pdf}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => window.print()}
                  data-testid="analisis-print-btn"
                  title="Cetak / Print"
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md"
                >
                  <Printer className="h-4 w-4" />
                  {lang === "en" ? "Print" : "Cetak"}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || !selected}
                  data-testid="analisis-download-generated-pdf"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md disabled:opacity-60"
                >
                  {downloadingPdf ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4" />
                  )}
                  {downloadingPdf
                    ? lang === "en"
                      ? "Preparing..."
                      : "Menyiapkan..."
                    : lang === "en"
                    ? "Download PDF"
                    : "Unduh PDF"}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-16 flex items-center justify-center text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : error ? (
              <div className="p-8 rounded-3xl bg-red-50 text-red-700 border border-red-100">
                {String(error)}
              </div>
            ) : !selected ? (
              <EmptyDataBanner
                lang={lang}
                testid="analisis-empty-data"
                title={
                  lang === "en"
                    ? `No data for ${year}`
                    : `Belum ada data untuk tahun ${year}`
                }
                lines={
                  lang === "en"
                    ? [
                        "This period may not be available yet in the hospital SIRS/SIMRS.",
                        "Try picking another Year from the selector above.",
                        "In production, make sure SIMRS already contains data for this period.",
                      ]
                    : [
                        "Data periode ini mungkin belum tersedia di SIRS/SIMRS rumah sakit.",
                        "Coba pilih Tahun lain pada pemilih di atas.",
                        "Di production, pastikan SIMRS sudah memuat data untuk periode tersebut.",
                      ]
                }
              />
            ) : (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                {/* API data-flow inspector — SUPERADMIN only, hidden when printing.
                    All SIPERKASA sources are served via SIRS_API_BASE_URL. */}
                {isSuperadmin && (
                  <div className="no-print" data-testid="sirs-api-flow-superadmin">
                    <SirsApiFlow sub={sub} year={selected.year} month={selected.month} />
                  </div>
                )}

                <div ref={contentRef} className="space-y-6">
                  {sub === "kunjungan" && (
                    <KunjunganAnalisisView entry={selected} all={items} t={t} lang={lang} />
                  )}
                  {sub === "bor" && (
                    <BORView entry={selected} all={items} t={t} lang={lang} />
                  )}
                  {sub === "pegawai" && (
                    <PegawaiView entry={selected} all={items} t={t} lang={lang} />
                  )}
                  {(sub === "lab" || sub === "radiologi") && (
                    <KunjunganAnalisisView
                      entry={selected}
                      all={items}
                      t={t}
                      lang={lang}
                      breakdownTitle={t.laporan.penunjang_breakdown}
                    />
                  )}
                  {sub === "apotek" && (
                    <ApotekView entry={selected} all={items} t={t} lang={lang} />
                  )}

                  {/* Year-over-year comparison card. Kept `no-print` so the browser
                      Cetak stays unchanged, but it lives INSIDE contentRef so the
                      generated "Unduh PDF" (html2canvas, ignores @media print)
                      includes it. */}
                  {compareOn &&
                    !compareLoading &&
                    year !== compareYear &&
                    compareItems.length > 0 && (
                      <div className="no-print" data-testid="analisis-compare-section">
                        <YearComparisonCard
                          sub={sub}
                          itemsA={items}
                          yearA={year}
                          itemsB={compareItems}
                          yearB={compareYear}
                          lang={lang}
                        />
                      </div>
                    )}
                </div>

                {/* Transient comparison states — screen only, excluded from the PDF
                    (rendered outside contentRef). */}
                {compareOn &&
                  (compareLoading || year === compareYear || compareItems.length === 0) && (
                    <div className="no-print">
                      {compareLoading ? (
                        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-10 flex items-center justify-center text-slate-300">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      ) : year === compareYear ? (
                        <div
                          data-testid="analisis-compare-same-year"
                          className="rounded-3xl bg-amber-50 border border-amber-200 text-amber-800 p-6 text-sm"
                        >
                          {lang === "en"
                            ? "Pick a different comparison year to see two years side by side."
                            : "Pilih tahun pembanding yang berbeda untuk melihat dua tahun berdampingan."}
                        </div>
                      ) : (
                        <EmptyDataBanner
                          lang={lang}
                          testid="analisis-compare-empty"
                          title={
                            lang === "en"
                              ? `No data for ${compareYear}`
                              : `Belum ada data untuk tahun ${compareYear}`
                          }
                          lines={
                            lang === "en"
                              ? [
                                  "The comparison year has no data in the hospital SIRS/SIMRS yet.",
                                  "Pick a different comparison year above.",
                                ]
                              : [
                                  "Tahun pembanding ini belum memiliki data di SIRS/SIMRS rumah sakit.",
                                  "Pilih tahun pembanding lain di atas.",
                                ]
                          }
                        />
                      )}
                    </div>
                  )}
              </motion.div>
            )}

            {selected?.note && (
              <div className="mt-6 text-xs text-slate-400 italic">{selected.note}</div>
            )}

            {sirsMeta && isSuperadmin && (
              <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-slate-400 no-print" data-testid="sirs-meta-superadmin">
                <div className="flex items-center gap-2">
                  <Server className="h-3.5 w-3.5" />
                  <span>
                    Data disediakan oleh <span className="font-mono text-slate-600">{sirsMeta.source}</span>
                    {" "}(simulated hospital information system).
                  </span>
                </div>
                {selected && (
                  <code
                    data-testid="sirs-endpoint-ref"
                    className="px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-600 font-mono text-[10px]"
                  >
                    GET /api/sirs/{sub}?year={selected.year}&month={selected.month}
                  </code>
                )}
              </div>
            )}

            {/* Print footer — visible only on print */}
            <div className="print-only mt-10 pt-6 border-t border-slate-300 text-xs text-slate-700">
              <div className="flex justify-between items-start gap-6">
                <div>
                  <div>
                    Dicetak pada:{" "}
                    <span className="font-semibold">
                      {new Date().toLocaleString("id-ID", {
                        dateStyle: "full",
                        timeStyle: "short",
                      })}
                    </span>
                  </div>
                  {sirsMeta && isSuperadmin && (
                    <div className="mt-1">
                      Sumber data:{" "}
                      <span className="font-mono">{sirsMeta.source}</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div>Tarakan, ______________________</div>
                  <div className="mt-16">(_____________________________)</div>
                  <div className="text-[10px] mt-1">Nama & Tanda Tangan</div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    </AuthGate>
  );
};

export default AnalisisDetail;
